import { Heading06 } from 'module/ui/components/styles';
import { Editor as TiptapEditorType, JSONContent } from '@tiptap/core';
import FileHandler from '@tiptap/extension-file-handler';
import FontSize from '@tiptap/extension-font-size';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { TableRow, Table } from '@tiptap/extension-table';
import {
  getHierarchicalIndexes,
  TableOfContentDataItem,
  TableOfContents,
} from '@tiptap/extension-table-of-contents';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Underline } from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { CustomTableCellExtension } from 'components/organisms/document-editor/extensions/table/table-cell/extension';
import {
  CustomTableHeader,
  tableHeaderLeafNode,
} from 'components/organisms/document-editor/extensions/table/table-header/extension';
import './styles.css';
import { WrapParagraphsExtension } from 'components/organisms/document-editor/extensions/table/table-header/wrapper-extension';
import { TiptapContentStyles } from './styles/content-styles';
import { ToCLineIndicator } from './ToCLineIndicator';
import { FloatingToolbar as DefaultFloatingToolbar } from './toolbar/default-toolbar';
import {
  defaultFloatingToolbarConfig,
  RichTextEditorProps,
  RichTextEditorValueType,
} from './types';
import {
  isCustomToolbarConfig,
  isEditorContentEqual,
  isStringContent,
  isTiptapJsonEmpty,
} from './utils/common';

// Extends @tiptap/extension-image to persist `attachmentId`. The `src` is a
// short-lived presigned URL minted at upload time and re-minted on document
// load; `attachmentId` is the stable identifier that survives reloads.
const ImageWithAttachmentId = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      attachmentId: {
        default: null,
        parseHTML: (element) => {
          const raw = element.getAttribute('data-attachment-id');

          return raw == null ? null : Number(raw);
        },
        renderHTML: (attributes) => {
          if (attributes.attachmentId == null) {
            return {};
          }

          return { 'data-attachment-id': String(attributes.attachmentId) };
        },
      },
      width: {
        default: null,
        parseHTML: (element) => {
          const raw = element.getAttribute('width');

          return raw == null ? null : Number(raw);
        },
        renderHTML: (attributes) => {
          if (attributes.width == null) {
            return {};
          }

          return { width: String(attributes.width) };
        },
      },
      height: {
        default: null,
        parseHTML: (element) => {
          const raw = element.getAttribute('height');

          return raw == null ? null : Number(raw);
        },
        renderHTML: (attributes) => {
          if (attributes.height == null) {
            return {};
          }

          return { height: String(attributes.height) };
        },
      },
    };
  },
});

const DocumentTitle = styled.div`
  ${Heading06};
  padding: var(--spacing-05);
  width: 768px;
`;

const Editor = styled(EditorContent)`
  display: flex;
  flex-grow: 1;
  flex-direction: row;
  ${TiptapContentStyles};

  .rte-editor {
    height: auto;
    flex-grow: 1;
    max-height: 100%;
    overflow-y: auto;

    .is-editor-empty::before {
      color: var(--text-placeholder);
      content: attr(data-placeholder);
      float: left;
      height: 0;
      pointer-events: none;
    }
  }
`;

const RichTextEditorComponent = <ValueType extends RichTextEditorValueType>({
  value,
  placeholder,
  onChange,
  onInitialize,
  className,
  extensionConfig,
  floatingToolbarConfig = defaultFloatingToolbarConfig,
  // hasTableToolbar = false,
  readonly = false,
  isInsertOnlyMode = false,
  extensions: extensionsProp = [],
  editorOptions = {},
  isChatOpen = false,
  documentTitle,
}: RichTextEditorProps<ValueType>) => {
  const [currentContent, setCurrentContent] = useState<ValueType | null>(null);
  const [items, setItems] = useState<TableOfContentDataItem[]>([]);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const onImageFiles = extensionConfig?.imageConfig?.onImageFiles;

  // Custom scroll detection function
  const updateScrollState = useCallback(
    (itemsToUpdate?: TableOfContentDataItem[]) => {
      const currentItems = itemsToUpdate || items;

      if (!scrollContainerRef.current || currentItems.length === 0) {
        return;
      }

      const container = scrollContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      const containerTop = containerRect.top;

      const updatedItems = currentItems.map((item) => {
        const element = document.querySelector(`[data-toc-id="${item.id}"]`);

        if (!element) {
          return item;
        }

        const elementRect = element.getBoundingClientRect();
        const elementBottom = elementRect.bottom;

        // Element is scrolled over if it's above the visible area
        const isScrolledOver = elementBottom < containerTop;

        return {
          ...item,
          isScrolledOver,
        };
      });

      setItems(updatedItems);
    },
    [items],
  );

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        trailingNode: false,
      }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TextStyle,
      FontSize,
      TableOfContents.configure({
        getIndex: getHierarchicalIndexes,
        onUpdate(content) {
          // Store the scroll container reference
          const contentWrapper = document.querySelector(
            '.document-content-wrapper',
          );

          if (contentWrapper) {
            scrollContainerRef.current = contentWrapper as HTMLElement;
          }

          setItems(content as TableOfContentDataItem[]);

          // Update scroll state after a short delay to ensure DOM is ready
          setTimeout(() => {
            if (scrollContainerRef.current) {
              updateScrollState(content as TableOfContentDataItem[]);
            }
          }, 100);
        },
        scrollParent: () => {
          // Return the content wrapper for scroll detection
          const contentWrapper = document.querySelector(
            '.document-content-wrapper',
          ) as HTMLElement;

          return contentWrapper || window;
        },
      }),
      TaskList,
      TaskItem,
      Underline,
      // Wide tables in TABLE/RECORD custom sections: see CustomSectionTableLayoutExtension + content-styles.ts
      Table.configure({
        resizable: true,
      }),
      TableRow,
      CustomTableHeader.configure({
        editable: isInsertOnlyMode || !readonly,
      }),
      CustomTableCellExtension.configure({
        readonly: readonly,
        isInsertOnlyMode: isInsertOnlyMode,
      }),
      tableHeaderLeafNode,
      WrapParagraphsExtension,
      ImageWithAttachmentId.configure({
        resize: {
          enabled: true,
          directions: ['top', 'bottom', 'left', 'right'], // can be any direction or diagonal combination
          minWidth: 50,
          minHeight: 50,
          alwaysPreserveAspectRatio: true,
        },
      }),
      FileHandler.configure({
        allowedMimeTypes: ['image/png', 'image/jpeg', 'image/jpg'],
        onDrop: (currentEditor, files, pos) => {
          onImageFiles?.(currentEditor, files, pos);
        },
        onPaste: (currentEditor, files, htmlContent) => {
          if (files.length === 0 && htmlContent) {
            // No file blobs — this is a pure HTML/URL paste (e.g. an image
            // dragged from another tab). Let the default paste path handle it
            // so the original `src` is preserved as-is.
            return false;
          }
          onImageFiles?.(
            currentEditor,
            files,
            currentEditor.state.selection.anchor,
          );
        },
      }),
      ...extensionsProp,
    ],
    [
      extensionsProp,
      placeholder,
      readonly,
      isInsertOnlyMode,
      updateScrollState,
      onImageFiles,
    ],
  );

  // Create a stable key for extensions to trigger recreation
  const extensionKey = useMemo(() => {
    return extensions
      .map((ext) => ext.name)
      .sort()
      .join(',');
  }, [extensions]);

  const editor = useEditor(
    {
      extensions,
      content: currentContent ?? value ?? '',
      editable: !readonly,
      onCreate: ({ editor }) => {
        onInitialize?.(editor);
      },
      onUpdate: ({ editor, transaction }) => {
        const getValueForType = (
          editor: TiptapEditorType,
          valueType: ValueType | undefined,
        ): ValueType => {
          const hasNoContent =
            isTiptapJsonEmpty(editor.getJSON()) && isEmpty(editor.getText());

          if (valueType == null || isStringContent(valueType)) {
            return hasNoContent
              ? ('' as ValueType)
              : (editor.getHTML() as ValueType);
          }
          if (Array.isArray(valueType)) {
            return hasNoContent
              ? ([] as JSONContent[] as ValueType)
              : (editor.getJSON() as ValueType);
          }

          return hasNoContent
            ? ({ type: 'doc', content: [] } as JSONContent as ValueType)
            : (editor.getJSON() as ValueType);
        };

        const newContent = getValueForType(editor, value);

        setCurrentContent(newContent);
        onChange?.(newContent, editor, transaction);
      },
      ...editorOptions,
      editorProps: {
        attributes: {
          class: `${isInsertOnlyMode ? 'is-insert-only' : ''} rte-editor`,
          ...(readonly
            ? {
                style:
                  '-webkit-user-select: text; user-select: text; cursor: text;',
              }
            : {}),
        },
        ...editorOptions.editorProps,
      },
      injectCSS: true,
    },
    [extensionKey],
  ); // Recreate editor when extensions change

  useEffect(() => {
    editor?.setEditable(isInsertOnlyMode || !readonly);
  }, [readonly, editor, isInsertOnlyMode]);

  useEffect(() => {
    if (editor && value && !isEditorContentEqual(editor, value)) {
      editor?.commands.setContent(value);
      setCurrentContent(value);
    }
  }, [editor, value]);

  // Set up scroll listener for custom scroll detection
  useEffect(() => {
    const contentWrapper = document.querySelector('.document-content-wrapper');

    if (!contentWrapper) {
      return;
    }

    const handleScroll = () => {
      updateScrollState(); // This will use the current items from state
    };

    contentWrapper.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      contentWrapper.removeEventListener('scroll', handleScroll);
    };
  }, [updateScrollState]);

  const FloatingToolbar = useMemo(() => {
    if (!floatingToolbarConfig.enabled) {
      return null;
    }

    return isCustomToolbarConfig(floatingToolbarConfig)
      ? floatingToolbarConfig.toolbar
      : DefaultFloatingToolbar;
  }, [floatingToolbarConfig]);
  const toolbarConfig = useMemo(() => {
    if (
      !floatingToolbarConfig.enabled ||
      isCustomToolbarConfig(floatingToolbarConfig)
    ) {
      return undefined;
    }

    return floatingToolbarConfig;
  }, [floatingToolbarConfig]);

  if (!editor) {
    return null;
  }

  return (
    <div className="col-group">
      {extensionConfig?.tableOfContentsConfig?.enabled && !isChatOpen && (
        <div className="sidebar">
          <div className="sidebar-options">
            <ToCLineIndicator
              editor={editor}
              items={items as TableOfContentDataItem[]}
            />
          </div>
        </div>
      )}
      <div className="main">
        {documentTitle && <DocumentTitle>{documentTitle}</DocumentTitle>}
        <Editor editor={editor} className={className} />
        {editor && FloatingToolbar && (
          <FloatingToolbar editor={editor} config={toolbarConfig} />
        )}
      </div>
      {/* {editor && hasTableToolbar && <TableToolbar editor={editor} />} */}
    </div>
  );
};

const StyledEditor = styled(RichTextEditorComponent)``;

export const RichTextEditor: typeof RichTextEditorComponent = StyledEditor;
